import React from "react";
import { Link } from "react-router-dom";
import { Car } from "lucide-react";
import { TrustRating } from "@/components/TrustRating";
import { CITIES, CITY_PATH } from "@/lib/cities";
import { THEMATIC_PAGES } from "@/lib/thematic";
import { MODELS, MODEL_PATH } from "@/lib/models";
import { ARTICLES, BLOG_PATH } from "@/lib/blog";
import { DEPARTMENTS, DEPT_PATH } from "@/lib/departments";
import { REGIONS, REGION_PATH } from "@/lib/regions";
import { TRIPS, tripSlug, TRIP_PATH } from "@/lib/trips";
import { findCity } from "@/lib/cities";

// Internal-linking footer (SEO maillage). Used on content pages and, in a more
// discreet form, below the main GPS app. Pure links — no data fetching.
export const SeoFooter = ({ variant = "content" }) => {
  const year = new Date().getFullYear();
  const isApp = variant === "app";

  return (
    <footer
      className={
        "border-t border-border bg-card/60 " +
        (isApp ? "mt-0" : "mt-12")
      }
      data-testid="seo-footer"
      aria-label="Liens utiles vspo"
    >
      <div className="mx-auto max-w-5xl px-4 py-10">
        <TrustRating />
        <div className="mb-6 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Car className="h-4 w-4" />
          </span>
          <div className="leading-tight">
            <div className="font-display text-base font-bold text-foreground">vspo</div>
            <div className="text-[11px] text-muted-foreground">
              GPS gratuit pour voiture sans permis · 45 km/h
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Guides
            </h3>
            <ul className="space-y-2">
              {THEMATIC_PAGES.map((t) => (
                <li key={t.slug}>
                  <Link
                    to={t.path}
                    className="text-sm text-muted-foreground transition-colors hover:text-primary"
                    data-testid={`footer-thematic-${t.slug}`}
                  >
                    {t.kicker}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={BLOG_PATH}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  data-testid="footer-blog"
                >
                  Conseils VSP
                </Link>
              </li>
              <li>
                <Link
                  to="/a-propos"
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  data-testid="footer-about"
                >
                  À propos
                </Link>
              </li>
              <li>
                <Link
                  to="/premium"
                  className="text-sm font-medium text-amber-600 transition-colors hover:text-amber-500 dark:text-amber-400"
                  data-testid="footer-premium"
                >
                  Passer Premium
                </Link>
              </li>
              <li>
                <Link
                  to="/compte"
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  data-testid="footer-account"
                >
                  Mon compte
                </Link>
              </li>
              <li>
                <Link
                  to="/confidentialite"
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  data-testid="footer-privacy"
                >
                  Confidentialité
                </Link>
              </li>
              <li>
                <Link
                  to="/mentions-legales"
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  data-testid="footer-legal"
                >
                  Mentions légales
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Modèles
            </h3>
            <ul className="space-y-2">
              {MODELS.map((m) => (
                <li key={m.slug}>
                  <Link
                    to={`${MODEL_PATH}/${m.slug}`}
                    className="text-sm text-muted-foreground transition-colors hover:text-primary"
                    data-testid={`footer-model-${m.slug}`}
                  >
                    {m.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/comparatif-voiture-sans-permis"
                  className="text-sm font-medium text-primary transition-colors hover:underline"
                  data-testid="footer-compare"
                >
                  Comparatif des modèles
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Derniers conseils
            </h3>
            <ul className="space-y-2">
              {ARTICLES.slice(0, 5).map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`${BLOG_PATH}/${a.slug}`}
                    className="line-clamp-1 text-sm text-muted-foreground transition-colors hover:text-primary"
                    data-testid={`footer-article-${a.slug}`}
                  >
                    {a.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 md:col-span-1">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              GPS VSP par ville
            </h3>
            <div className="flex flex-wrap gap-x-3 gap-y-1.5">
              {CITIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`${CITY_PATH}/${c.slug}`}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  data-testid={`footer-city-${c.slug}`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 border-t border-border pt-6">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            VSP par région
          </h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
            {REGIONS.map((r) => (
              <Link
                key={r.slug}
                to={`${REGION_PATH}/${r.slug}`}
                className="text-sm text-muted-foreground transition-colors hover:text-primary"
                data-testid={`footer-region-${r.slug}`}
              >
                {r.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-8 border-t border-border pt-6">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            VSP par département
          </h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
            {DEPARTMENTS.map((d) => (
              <Link
                key={d.slug}
                to={`${DEPT_PATH}/${d.slug}`}
                className="text-sm text-muted-foreground transition-colors hover:text-primary"
                data-testid={`footer-dept-${d.slug}`}
              >
                {d.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-6 border-t border-border pt-6">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Itinéraires populaires
          </h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
            {TRIPS.map((t) => {
              const f = findCity(t.from);
              const d = findCity(t.to);
              if (!f || !d) return null;
              return (
                <Link
                  key={tripSlug(t)}
                  to={`${TRIP_PATH}/${tripSlug(t)}`}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  data-testid={`footer-trip-${tripSlug(t)}`}
                >
                  {f.name} → {d.name}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="mt-8 border-t border-border pt-4 text-xs text-muted-foreground">
          © {year} vspo — GPS gratuit et libre pour voiture sans permis (VSP) en France. Itinéraires
          45 km/h évitant autoroutes et voies rapides. Informations fournies à titre indicatif.
        </div>
      </div>
    </footer>
  );
};
